import { Typography, CircularProgress, Skeleton, Box } from '@mui/material';
import { useRecoilState } from 'recoil';
import {
  dateRangeState,
  dateValueState,
  siteState,
  cameraState,
  selectedOrdersState,
  selectedSpeciesState,
  dateRangeAdjustedState,
  speciesListState,
  speciesFilterModeState,
} from '../state/statistics.state';
import { WindFarmCard } from '../components/Dashboard/WindFarmCard';
import { StatsCard } from '../components/Dashboard/StatsCard';
import {
  GetSiteData,
  GetCameraData,
  GetSpeciesList,
} from '../hooks/statistics.hooks';
import {
  GetBirdsDetectedAtSiteWithDateRange,
  GetDashboardStats,
  GetLastProcessedDetection,
  GetUniqueSpeciesCount,
} from '../hooks/dashboard.hooks';
import { useInitializeSiteAndCamera } from '../hooks/useInitializeSiteAndCamera';
import { useFeatureFlag } from '../utils/featureFlagApi';
import { useOptimalDateRange } from '../hooks/useOptimalDateRange';
import { StaticticsStickyNavbar } from '../components/statistics/StatisticsStickyNavbar';

export default function Dashboard() {
  const oneMonthBack = new Date();
  oneMonthBack.setMonth(oneMonthBack.getMonth() - 1);

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const [dateValue, setDateValue] = useRecoilState(dateValueState);
  const [site, setSite] = useRecoilState(siteState);
  const [camera, setCamera] = useRecoilState(cameraState);
  const [dateRange, setDateRange] = useRecoilState(dateRangeState);
  const [selectedSpecies, setSelectedSpecies] = useRecoilState(selectedSpeciesState);
  const [selectedOrders, setSelectedOrders] = useRecoilState(selectedOrdersState);
  const [species, setSpecies] = useRecoilState(speciesListState);
  const [speciesFilterMode, setSpeciesFilterMode] = useRecoilState(speciesFilterModeState);
  const [hasAdjustedDateRange, setHasAdjustedDateRange] = useRecoilState(dateRangeAdjustedState);

  const { data: siteData, isLoading: siteDataLoading } = GetSiteData();
  const { data: cameraData } = GetCameraData(site);
  const { data: dashboardData, isLoading: dashboardDataLoading } = GetDashboardStats(site);
  const shouldFilterSpecies =
    speciesFilterMode === 'custom' && (selectedOrders.length > 0 || selectedSpecies.length > 0);
  const filteredOrders = shouldFilterSpecies ? selectedOrders : [];
  const filteredSpecies = shouldFilterSpecies ? selectedSpecies : [];
  
  GetSpeciesList(setSpecies, dateRange, site, camera);
  const { data: birdsDetectedAtSite, isLoading: birdsDetectedLoading } = GetBirdsDetectedAtSiteWithDateRange(
    dateRange,
    site,
    camera,
    filteredOrders,
    filteredSpecies,
  );
  const { data: lastProcessedData, isLoading: lastProcessedLoading } = GetLastProcessedDetection(site);
  const { data: uniqueSpeciesCount, isLoading: uniqueSpeciesLoading } = GetUniqueSpeciesCount(dateRange, site, camera);

  useInitializeSiteAndCamera();

  const updateCameraDetails = (event: any, newValue: any) => {
    setCamera(newValue);
  };

  const lastProcessedTimestamp = lastProcessedData?.data?.segment_timestamp ?? null;

  const { getLastDataReceivedDate } = useOptimalDateRange({
    lastProcessedTimestamp,
    hasAdjustedDateRange,
    setHasAdjustedDateRange,
  });

  const timeSinceLastUpdate = (() => {
    const lastDataDate = getLastDataReceivedDate();
    if (lastDataDate) {
      const hoursAgo = Math.round(Math.abs(Date.now() - lastDataDate.getTime()) / 3600000);
      return hoursAgo + ' h ago';
    }
    return 'N/A';
  })();

  const formatDate = (date: Date): string => {
    return date.getFullYear() + ' ' +
           MONTHS[date.getMonth()] + ' ' +
           String(date.getDate()).padStart(2, '0');
  };

  const latestSegmentUpdateDate = (() => {
    const lastDataDate = getLastDataReceivedDate();
    if (lastDataDate) {
      // Format as: 2025 Mar 13 9:19:43
      return lastDataDate.getFullYear() + ' ' +
             MONTHS[lastDataDate.getMonth()] + ' ' +
             String(lastDataDate.getDate()).padStart(2, '0') + ' ' +
             lastDataDate.getHours() + ':' +
             String(lastDataDate.getMinutes()).padStart(2, '0') + ':' +
             String(lastDataDate.getSeconds()).padStart(2, '0');
    }
    return 'No recent data';
  })();
  const companyId = dashboardData?.data?.[0]?.company_id || null;

  const company = (() => {
    const dataObject = dashboardData?.data?.[0];

    if (dataObject?.company_name) return dataObject.company_name;
    if (dataObject?.company_id__company_name) return dataObject.company_id__company_name;
    if (dataObject?.site_company_name) return dataObject.site_company_name;

    const currentSite = siteData?.data?.find((s: any) => s.id === parseInt(site));
    if (currentSite?.company_name) return currentSite.company_name;
    if (currentSite?.site_company_name) return currentSite.site_company_name;
    if (currentSite?.company_id__company_name) return currentSite.company_id__company_name;

    if (currentSite?.site_display_name && !currentSite.site_display_name.includes('Site')) {
      return currentSite.site_display_name;
    }

    return 'Company';
  })();

  const {
    data: isEnabled,
    isLoading: flagLoading,
    error: flagError,
  } = useFeatureFlag(companyId, 'company_2.0');

  return (
    <Box
      sx={{
        backgroundColor: '#F8F9FA',
        flexGrow: 1,
        paddingTop: '50px',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Box sx={{ width: '25%' }}></Box>
        <Box sx={{
          display: 'grid',
          flexDirection: 'column',
          rowGap: '8px',
          width: '1000px',
        }}>
          {siteDataLoading || dashboardDataLoading ? (
            <Skeleton variant="text" width="300px" height={40} style={{ marginBottom: '8px' }} />
          ) : (
            <Typography style={{
              fontSize: '32px',
              lineHeight: '40px',
              margin: 0,
              fontWeight: '600',
              color: '#17052D',
              letterSpacing: '-0.5px',
            }}>
              {flagLoading ? 'Loading...'
              :
              flagError ? company
              :
              isEnabled ? company + ' 2.0' : company
              }
            </Typography>
          )}
          <Typography style={{
            fontSize: '16px',
            lineHeight: '24px',
            color: '#6B7280',
            margin: '8px 0 0 0',
            fontWeight: '400',
          }}>
            Overview of recent bird activity and system status
          </Typography>
        </Box>
      </Box>

      <StaticticsStickyNavbar
        dateRange={dateRange}
        setDateRange={setDateRange}
        site={site}
        setSite={setSite}
        listOfSites={siteData}
        updateCameraDetails={updateCameraDetails}
        cameraData={cameraData}
        camera={camera}
        speciesList={species}
        setSelectedOrders={setSelectedOrders}
        setSelectedSpecies={setSelectedSpecies}
        selectedOrders={selectedOrders}
        selectedSpecies={selectedSpecies}
        dateValue={dateValue}
        setDateValue={setDateValue}
        showDownloadButtons={false}
        setDateRangeAdjusted={setHasAdjustedDateRange}
        setSpeciesFilterMode={setSpeciesFilterMode}
      />

      <Box sx={{ display: 'flex', width: '100%' }}>
        <Box sx={{ width: '25%' }}></Box>
        <Box sx={{
          marginTop: '24px',
          marginBottom: '48px',
          display: 'flex',
          flexDirection: 'column',
          rowGap: '32px',
          width: '1000px',
        }}>
          {dashboardDataLoading ? (
            <Box sx={{
              height: '240px',
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(229, 231, 235, 0.8)',
              background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
            }}>
              <CircularProgress size={40} style={{ color: '#45009C' }} />
            </Box>
          ) : (
            <WindFarmCard data={dashboardData?.data} />
          )}
          <Box sx={{ display: 'flex', gap: '30px' }}>
            {lastProcessedLoading ? (
              <Box sx={{
                height: '170px',
                flex: 1,
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(229, 231, 235, 0.8)',
                background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
              }}>
                <Skeleton variant="text" width="80%" height={20} />
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={24} style={{ color: '#45009C' }} />
                </Box>
                <Skeleton variant="text" width="60%" height={16} />
              </Box>
            ) : (
              <StatsCard
                header="Last data received"
                data={timeSinceLastUpdate}
                description={latestSegmentUpdateDate}
              />
            )}
            {birdsDetectedLoading ? (
              <Box sx={{
                height: '170px',
                flex: 1,
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(229, 231, 235, 0.8)',
                background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
              }}>
                <Skeleton variant="text" width="80%" height={20} />
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={24} style={{ color: '#45009C' }} />
                </Box>
                <Skeleton variant="text" width="60%" height={16} />
              </Box>
            ) : (
              <StatsCard
                header="Birds detected at site"
                data={String(birdsDetectedAtSite || 0)}
                description={(() => {
                  if (dateValue === 1) return "Last 30 days";
                  if (dateValue === 2) return "Last 90 days";
                  if (dateValue === 3 && dateRange.startDate && dateRange.endDate) {
                    const start = formatDate(dateRange.startDate);
                    const end = formatDate(dateRange.endDate);
                    return `${start} - ${end}`;
                  }
                  return "Selected period";
                })()}
              />
            )}
            {uniqueSpeciesLoading ? (
              <Box sx={{
                height: '170px',
                flex: 1,
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(229, 231, 235, 0.8)',
                background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
              }}>
                <Skeleton variant="text" width="80%" height={20} />
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={24} style={{ color: '#45009C' }} />
                </Box>
                <Skeleton variant="text" width="60%" height={16} />
              </Box>
            ) : (
              <StatsCard
                header="Unique bird species"
                data={String(uniqueSpeciesCount || 0)}
                description={(() => {
                  if (dateValue === 1) return "Last 30 days";
                  if (dateValue === 2) return "Last 90 days";
                  if (dateValue === 3 && dateRange.startDate && dateRange.endDate) {
                    const start = formatDate(dateRange.startDate);
                    const end = formatDate(dateRange.endDate);
                    return `${start} - ${end}`;
                  }
                  return "Selected period";
                })()}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
